import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import LogInModal from 'flarum/forum/components/LogInModal';
import BelgiumMap from './BelgiumMap';
import { inFrame } from '../data/belgium';
import Lightbox from './Lightbox';
import ClubLivesets from './ClubLivesets';
import TranslationNotice from './TranslationNotice';
import { formatDate, formatDuration } from '../utils/format';
import { mediaCredit } from '../utils/credit';
import { t, tt, route, find, syncLocale, label, contentLang } from '../i18n';

// Hoeveel items een sectie toont voor "toon alles".
const PREVIEW = { flyers: 12, photos: 12, tracks: 12, articles: 6, videos_beeld: 6, videos_set: 6 };
// Langere verhalen worden ingeklapt tot de lezer ze openklapt.
const LONG_HISTORY = 4000;

// Archiefgroepen uit media.json, in deze volgorde.
const ARCHIVE_GROUPS = ['cards', 'cds', 'merch', 'logos', 'other'];

/**
 * /clubs/:slug — één club: intro, verhaal, tijdlijn, flyers, foto's, muziek, archief en de discussies erover.
 */
export default class ClubPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.expanded = {};
    this.lightbox = null;
    this.active = null;
    this.onscroll = () => this.spy();
    syncLocale();
    this.load(m.route.param('slug'));
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    window.addEventListener('scroll', this.onscroll, { passive: true });
  }

  onremove(vnode) {
    super.onremove(vnode);
    window.removeEventListener('scroll', this.onscroll);
  }

  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    const slug = m.route.param('slug');
    if (this.club && slug !== this.club.slug()) {
      this.expanded = {};
      this.lightbox = null;
      this.load(slug);
    }
  }

  load(slug) {
    this.club = app.store.all('lc-clubs').find((c) => c.slug() === slug && c.historyHtml() !== undefined) || null;
    this.discussions = null;
    this.playing = {};

    if (this.club) this.ready();

    find('lc-clubs', slug).then((club) => {
      this.club = club;
      this.ready();
      m.redraw();
    });

    // Lijst van alle clubs voor "in de buurt" en de kaart.
    if (app.store.all('lc-clubs').length < 20) find('lc-clubs').then(() => m.redraw());
  }

  ready() {
    const club = this.club;
    app.history.push('lc.club', club.name());
    app.setTitle(club.name());
    app.setTitleCount(0);

    const tag = club.tag();
    if (tag && (!this.discussions || this.discussions.params.filter.tag !== tag.slug())) {
      this.discussions = new DiscussionListState({ filter: { tag: tag.slug() }, sort: 'top' });
      this.discussions.refresh();
    }
  }

  media() {
    return this.club.media() || {};
  }

  /** Secties die op deze pagina iets te tonen hebben, voor de navigatiebalk. */
  sections() {
    const club = this.club;
    const media = this.media();
    const archive = ARCHIVE_GROUPS.some((g) => (media[g] || []).length) || (media.articles || []).length || (media.video || []).length;

    return [
      club.historyHtml() && { id: 'verhaal', label: t('club.nav.story') },
      (club.timeline() || []).length && { id: 'tijdlijn', label: t('club.nav.timeline') },
      (media.flyers || []).length && { id: 'flyers', label: t('club.nav.flyers'), count: media.flyers.length },
      (media.photos || []).length && { id: 'fotos', label: t('club.nav.photos'), count: media.photos.length },
      ((club.tracks() || []).length || (media.audio || []).length) && { id: 'muziek', label: t('club.nav.music') },
      (((club.livesets() || {}).sets || []).length || ((club.livesets() || {}).collections || []).length) && { id: 'livesets', label: t('club.nav.livesets'), count: ((club.livesets() || {}).sets || []).length || null },
      (club.videos() || []).length && { id: 'videos', label: t('club.nav.videos'), count: club.videos().length },
      archive && { id: 'archief', label: t('club.nav.archive') },
      { id: 'forum', label: t('club.nav.forum') },
    ].filter(Boolean);
  }

  view() {
    const club = this.club;
    if (!club) return <div className="LcClubPage"><LoadingIndicator /></div>;

    const media = this.media();
    const sections = this.sections();

    return (
      <div className="LcClubPage">
        {this.hero()}

        {sections.length > 2 ? (
          <nav className="LcClubNav" aria-label={tt('club.nav.label')}>
            <div className="container">
              <ul>
                {sections.map((s) => (
                  <li className={this.active === s.id ? 'is-active' : ''}>
                    <a href={'#' + s.id} onclick={(e) => this.jump(e, s.id)}>
                      {s.label}
                      {s.count ? <span className="LcClubNav-count">{s.count}</span> : null}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        ) : null}

        <div className="container LcClubBody">
          <main className="LcClubMain">
            {this.story()}
            {this.timeline()}
            {this.darkPages ? this.darkPages() : null}
            {this.gallery('flyers', media.flyers, 'LcGallery--flyers')}
            {this.gallery('fotos', media.photos, 'LcGallery--photos')}
            {this.music()}
            <ClubLivesets club={club} />
            {this.videos()}
            {this.archive()}
            {this.forum()}
          </main>

          <aside className="LcClubSide">{this.sidebar()}</aside>
        </div>

        {this.lightbox ? (
          <Lightbox
            items={this.lightbox.items}
            index={this.lightbox.index}
            base={media.base}
            onclose={() => {
              this.lightbox = null;
              m.redraw();
            }}
          />
        ) : null}
      </div>
    );
  }

  // ---------------------------------------------------------------- hero

  hero() {
    const club = this.club;
    const media = this.media();
    const flyers = media.flyers || [];
    const cover = this.cover();

    const place = [club.city(), club.isAbroad() ? club.country() : label('provinces', club.province())].filter(Boolean).join(', ');
    const facts = [
      place && { icon: 'fas fa-map-marker-alt', text: place },
      club.years() && { icon: 'far fa-calendar', text: club.years() },
      club.type() && { icon: 'fas fa-compact-disc', text: label('club_types', club.type()) },
    ].filter(Boolean);

    const stats = [
      flyers.length && { n: flyers.length, label: t('club.stats.flyers') },
      (media.photos || []).length && { n: media.photos.length, label: t('club.stats.photos') },
      (club.tracks() || []).length && { n: club.tracks().length, label: t('club.stats.tracks') },
      club.discussionCount() && { n: club.discussionCount(), label: t('club.stats.discussions') },
    ].filter(Boolean);

    // Rechts de kaart met de plaats(en) van de club; enkel voor clubs buiten het kaartkader de flyerwaaier.
    const places = this.mapPlaces();
    const fan = places.length ? [] : this.fanFlyers();

    return (
      <section className={'LcClubHero' + (cover ? ' LcClubHero--cover' : '')}>
        {cover ? <div className="LcClubHero-bg" style={{ backgroundImage: `url("${media.base + cover.src}")` }} /> : null}
        <div className="container LcClubHero-inner">
          <div className="LcClubHero-text">
            <Link className="LcBack" href={route('lc.clubs')}>
              <i className="fas fa-arrow-left" aria-hidden="true" /> {t('club.back')}
            </Link>
            <h1 className="LcClubHero-title">{club.name()}</h1>
            {club.aliases() && club.aliases().length ? <p className="LcAliases">{t('common.aliases', { names: club.aliases().join(' · ') })}</p> : null}
            <ul className="LcFacts">
              {facts.map((f) => (
                <li>
                  <i className={f.icon} aria-hidden="true" /> {f.text}
                </li>
              ))}
            </ul>
            {club.description() ? <p className="LcClubHero-lead" lang={contentLang(club)}>{club.description()}</p> : null}
            <TranslationNotice model={club} />
            {club.genres() && club.genres().length ? (
              <p className="LcCard-genres">{club.genres().map((g) => <span className="LcChip">{g}</span>)}</p>
            ) : null}
            {stats.length ? (
              <dl className="LcStats LcStats--club">
                {stats.map((s) => (
                  <div>
                    <dt>{s.label}</dt>
                    <dd>{s.n}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>

          {fan.length ? (
            <div className="LcClubHero-fan" aria-label={tt('club.nav.flyers')}>
              {fan.map((f, i) => (
                <button
                  className={`LcFan-item LcFan-item--${i}`}
                  onclick={() => this.open(flyers, flyers.indexOf(f))}
                  aria-label={f.caption || tt('club.nav.flyers')}
                >
                  <img src={media.base + f.thumb} alt="" />
                </button>
              ))}
            </div>
          ) : places.length ? (
            this.heroMap(places)
          ) : null}
        </div>
      </section>
    );
  }

  /** Achtergrond van de hero: aangeduide cover, anders de grootste flyer. */
  cover() {
    const media = this.media();
    const all = [...(media.flyers || []), ...(media.photos || [])];
    return all.find((i) => i.cover) || [...(media.flyers || [])].sort((a, b) => b.w * b.h - a.w * a.h)[0] || null;
  }

  /** Plaatsen van de club die op de kaart van België vallen (één, of meerdere bij verhuizingen). */
  mapPlaces() {
    return (this.club.places() || []).filter((p) => p.lat && p.lng && inFrame(p.lat, p.lng));
  }

  /** Kaart rechts in de hero, met eronder de plaats of een genummerde lijst van plaatsen. */
  heroMap(places) {
    const club = this.club;
    const span = (p) => (p.from || p.to ? (p.from === p.to || !p.to ? String(p.from || p.to) : `${p.from || '?'}–${p.to}`) : '');
    const osm = (p) => `https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lng}#map=14/${p.lat}/${p.lng}`;

    return (
      <figure className="LcClubHero-map">
        <BelgiumMap places={places} compact />
        {places.length > 1 ? (
          <ol className="LcPlaces">
            {places.map((p, i) => (
              <li>
                <span className="LcPlaces-n">{i + 1}</span>
                <div>
                  <span className="LcPlaces-city">{p.city}</span>
                  {span(p) ? <span className="LcPlaces-years">{span(p)}</span> : null}
                  {p.name ? (
                    <span className="LcPlaces-name">{p.slug ? <Link href={route('lc.club', { slug: p.slug })}>{p.name}</Link> : p.name}</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <figcaption className="LcPlaces-single">
            <span>{[club.address() || club.city(), label('provinces', club.province())].filter(Boolean).join(' · ')}</span>
            <a href={osm(places[0])} target="_blank" rel="noopener">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> {t('club.open_map')}
            </a>
          </figcaption>
        )}
      </figure>
    );
  }

  /** Drie staande flyers voor de waaier in de hero, aangeduide eerst. */
  fanFlyers() {
    const flyers = (this.media().flyers || []).filter((f) => f.h >= f.w * 1.1);
    const picked = flyers.filter((f) => f.fan);
    const rest = flyers.filter((f) => !f.fan).sort((a, b) => b.w * b.h - a.w * a.h);
    return [...picked, ...rest].slice(0, 3);
  }

  // ---------------------------------------------------------------- secties

  story() {
    const club = this.club;
    if (!club.historyHtml() && !club.yearsNote()) return null;

    const long = (club.historyHtml() || '').length > LONG_HISTORY;
    const open = !long || this.expanded.story;

    return (
      <section className="LcSection LcHistory" id="verhaal">
        <h2>{t('club.history')}</h2>
        {club.historyHtml() ? (
          <div className={'LcHistory-body' + (open ? '' : ' is-collapsed')} lang={contentLang(club)}>
            {m.trust(club.historyHtml())}
          </div>
        ) : null}
        {long ? (
          <Button className="Button LcMore" icon={open ? 'fas fa-chevron-up' : 'fas fa-book-open'} onclick={() => this.toggle('story', 'verhaal')}>
            {open ? t('club.read_less') : t('club.read_more')}
          </Button>
        ) : null}
        {club.yearsNote() ? (
          <details className="LcDetails">
            <summary>{t('club.years_note')}</summary>
            <p lang={contentLang(club)}>{club.yearsNote()}</p>
          </details>
        ) : null}
        {club.historySource() ? <p className="LcSource">{t('club.history_source', { source: club.historySource() })}</p> : null}
      </section>
    );
  }

  timeline() {
    const items = this.club.timeline() || [];
    if (!items.length) return null;

    return (
      <section className="LcSection" id="tijdlijn">
        <h2>{t('club.timeline')}</h2>
        <ol className="LcTimeline" lang={contentLang(this.club)}>
          {items.map((i) => (
            <li>
              <span className="LcTimeline-year">{i.year}</span>
              <p>{i.text}</p>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  gallery(id, items, className) {
    if (!items || !items.length) return null;
    const key = id === 'fotos' ? 'photos' : id;
    const base = this.media().base;
    const shown = this.expanded[key] ? items : items.slice(0, PREVIEW[key]);

    return (
      <section className="LcSection" id={id}>
        <div className="LcSection-head">
          <h2>{t(`club.${key}`)}</h2>
          <span className="LcSection-count">{items.length}</span>
        </div>
        {id === 'flyers' ? <p className="LcNote">{t('club.flyers_note')}</p> : null}
        <ul className={'LcGallery ' + className}>
          {shown.map((item) => (
            <li>
              <button className="LcGallery-item" onclick={() => this.open(items, items.indexOf(item))}>
                <img src={base + item.thumb} alt={item.caption || ''} loading="lazy" width={item.w} height={item.h} />
                {item.date ? <span className="LcGallery-date">{formatDate(item.date)}</span> : null}
              </button>
              {item.caption ? <p className="LcGallery-caption">{item.caption}</p> : null}
              {mediaCredit(item, 'LcGallery-credit')}
            </li>
          ))}
        </ul>
        {this.moreButton(key, id, items.length)}
      </section>
    );
  }

  music() {
    const club = this.club;
    const media = this.media();
    const tracks = club.tracks() || [];
    const audio = media.audio || [];
    if (!tracks.length && !audio.length) return null;

    const shown = this.expanded.tracks ? tracks : tracks.slice(0, PREVIEW.tracks);

    return (
      <section className="LcSection LcTracks" id="muziek">
        <h2>{t('club.music')}</h2>

        {audio.length ? (
          <div className="LcAudioList">
            {audio.map((a) => (
              <figure className="LcAudio">
                <div className="LcAudio-head">
                  <i className="fas fa-broadcast-tower" aria-hidden="true" />
                  <figcaption>
                    <strong>{a.caption || t('club.radio_spot')}</strong>
                    {a.duration ? <span>{formatDuration(a.duration)}</span> : null}
                  </figcaption>
                </div>
                <audio controls preload="none" src={media.base + a.src} />
              </figure>
            ))}
          </div>
        ) : null}

        {tracks.length ? (
          <div className="LcTracks-list">
            <div className="LcSection-head">
              <h3>{t('club.tracks')}</h3>
              <span className="LcSection-count">{tracks.length}</span>
            </div>
            <p className="LcNote">{t('club.tracks_note')}</p>
            <ul className="LcTrackGrid">
              {shown.map((track) => (
                <li className="LcTrack">
                  <a className="LcTrack-thumb" href={`https://www.youtube.com/watch?v=${track.youtube_id}`} target="_blank" rel="noopener noreferrer">
                    <img src={`https://img.youtube.com/vi/${track.youtube_id}/hqdefault.jpg`} alt={`${track.artist} – ${track.title}`} loading="lazy" />
                    <span className="LcTrack-play">
                      <i className="fas fa-play" aria-hidden="true" />
                    </span>
                  </a>
                  <div className="LcTrack-caption">
                    <strong>
                      {(track.artist_slugs || []).length === 1 ? (
                        <Link href={route('lc.artist', { slug: track.artist_slugs[0] })}>{track.artist}</Link>
                      ) : (
                        track.artist
                      )}
                    </strong>
                    <span>{track.title}</span>
                  </div>
                </li>
              ))}
            </ul>
            {this.moreButton('tracks', 'muziek', tracks.length)}
          </div>
        ) : null}
      </section>
    );
  }

  /** Video's van YouTube: eerst beelden uit de club (reportages, homevideo's), dan live-opnames van sets. */
  videos() {
    const videos = this.club.videos() || [];
    if (!videos.length) return null;

    const groups = [
      ['beeld', videos.filter((v) => v.kind !== 'set')],
      ['set', videos.filter((v) => v.kind === 'set')],
    ].filter(([, list]) => list.length);

    return (
      <section className="LcSection LcClubVideos" id="videos">
        <div className="LcSection-head">
          <h2>{t('club.videos')}</h2>
          <span className="LcSection-count">{videos.length}</span>
        </div>
        <p className="LcNote">{t('club.videos_note')}</p>
        {groups.map(([kind, list]) => {
          const key = 'videos_' + kind;
          return (
            <div className="LcVideoGroup">
              {groups.length > 1 ? <h3>{t(`club.video_kind.${kind}`)}</h3> : null}
              <ul className="LcVideoGrid">
                {(this.expanded[key] ? list : list.slice(0, PREVIEW[key])).map((v) => (
                  <li className="LcVideo" key={v.id}>
                    {this.videoPlayer(v)}
                    <div className="LcVideo-caption">
                      <strong>{v.caption || v.title}</strong>
                      <span className="LcVideo-meta">
                        {[v.date ? formatDate(v.date) : null, v.channel].filter(Boolean).join(' · ')}
                        {v.date || v.channel ? ' · ' : ''}
                        <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer">
                          {t('club.on_youtube')}
                        </a>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              {this.moreButton(key, 'videos', list.length)}
            </div>
          );
        })}
      </section>
    );
  }

  /** Miniatuur die pas bij een klik de YouTube-speler laadt (geen cookies of trage iframes vooraf). */
  videoPlayer(v) {
    if (this.playing[v.id]) {
      return (
        <div className="LcVideo-frame">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`}
            title={v.caption || v.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowfullscreen
          />
        </div>
      );
    }

    const inner = [
      <img src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`} alt="" loading="lazy" />,
      <span className="LcTrack-play">
        <i className="fas fa-play" aria-hidden="true" />
      </span>,
      v.duration ? <span className="LcVideo-duration">{formatDuration(v.duration)}</span> : null,
    ];

    // Video's die niet ingesloten mogen worden openen op YouTube zelf.
    return v.embed === false ? (
      <a className="LcVideo-thumb" href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" aria-label={tt('club.play_video', { title: v.caption || v.title })}>
        {inner}
      </a>
    ) : (
      <button className="LcVideo-thumb" onclick={() => (this.playing[v.id] = true)} aria-label={tt('club.play_video', { title: v.caption || v.title })}>
        {inner}
      </button>
    );
  }

  archive() {
    const media = this.media();
    const groups = ARCHIVE_GROUPS.filter((g) => (media[g] || []).length);
    const articles = media.articles || [];
    const videos = media.video || [];
    if (!groups.length && !articles.length && !videos.length) return null;

    return (
      <section className="LcSection" id="archief">
        <h2>{t('club.archive')}</h2>
        <p className="LcNote">{t('club.archive_note')}</p>

        {groups.map((g) => (
          <div className="LcArchiveGroup">
            <h3>{t(`club.media.${g}`)}</h3>
            <ul className={`LcGallery LcGallery--${g}`}>
              {media[g].map((item, i) => (
                <li>
                  <button className="LcGallery-item" onclick={() => this.open(media[g], i)}>
                    <img src={media.base + item.thumb} alt={item.caption || ''} loading="lazy" />
                  </button>
                  {item.caption ? <p className="LcGallery-caption">{item.caption}</p> : null}
                  {mediaCredit(item, 'LcGallery-credit')}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {videos.length ? (
          <div className="LcArchiveGroup">
            <h3>{t('club.media.video')}</h3>
            <div className="LcVideos">
              {videos.map((v) => (
                <figure>
                  <video controls preload="none" poster={v.poster ? media.base + v.poster : undefined} src={media.base + v.src} />
                  {v.caption ? <figcaption>{v.caption}</figcaption> : null}
                </figure>
              ))}
            </div>
          </div>
        ) : null}

        {articles.length ? (
          <div className="LcArchiveGroup">
            <h3>{t('club.media.articles')}</h3>
            {(this.expanded.articles ? articles : articles.slice(0, PREVIEW.articles)).map((a) => (
              <details className="LcArticle">
                <summary>
                  <i className="far fa-newspaper" aria-hidden="true" /> {a.caption || a.title}
                  {a.date ? <span className="LcArticle-date">{formatDate(a.date)}</span> : null}
                </summary>
                <div className="LcArticle-body">{m.trust(a.html)}</div>
              </details>
            ))}
            {this.moreButton('articles', 'archief', articles.length)}
          </div>
        ) : null}
      </section>
    );
  }

  forum() {
    const club = this.club;

    return (
      <section className="LcSection LcDiscussions" id="forum">
        <div className="LcDiscussions-head">
          <h2>{t('club.discussions', { name: club.name() })}</h2>
          {club.tag() ? (
            <Button className="Button Button--primary" icon="fas fa-pen" onclick={() => this.compose()}>
              {t('club.start_discussion')}
            </Button>
          ) : null}
        </div>
        {club.tag() ? <p className="LcNote">{t('club.start_discussion_note', { name: club.name() })}</p> : null}
        {this.discussions ? <DiscussionList state={this.discussions} /> : <p className="LcEmpty">{t('club.no_discussions')}</p>}
      </section>
    );
  }

  sidebar() {
    const club = this.club;
    const people = club.people() || [];
    const media = this.media();
    // Het hoofdlogo (wordmark) komt in het feitenblok; de naam staat in de hero.
    const logo = media.wordmark || (media.logos || [])[0];

    const facts = [
      [t('club.fact.address'), club.address()],
      [t('club.fact.place'), [club.city(), club.municipality() && club.municipality() !== club.city() ? `(${club.municipality()})` : null].filter(Boolean).join(' ')],
      [t('club.fact.province'), club.isAbroad() ? club.country() : label('provinces', club.province())],
      [t('club.fact.years'), club.years()],
      [t('club.fact.type'), label('club_types', club.type())],
    ].filter(([, v]) => v);

    return [
      <section className="LcPanel LcPanel--facts">
        {logo ? <img className="LcPanel-logo" src={media.base + logo.src} alt={club.name()} /> : null}
        <h3>{t('club.facts')}</h3>
        <dl className="LcFactList">
          {facts.map(([k, v]) => [<dt>{k}</dt>, <dd>{v}</dd>])}
        </dl>
      </section>,

      this.sitesPanel(),

      people.length ? (
        <section className="LcPanel">
          <h3>{t('club.people')}</h3>
          <ul className="LcPeople" lang={contentLang(club)}>
            {people.map((p) => (
              <li>
                <strong>{this.personName(p)}</strong>
                {p.role ? <span>{p.role}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      club.knownFor() && club.knownFor().length ? (
        <section className="LcPanel">
          <h3>{t('club.known_for')}</h3>
          <ul lang={contentLang(club)}>{club.knownFor().map((k) => <li>{k}</li>)}</ul>
        </section>
      ) : null,

      this.nearby().length ? (
        <section className="LcPanel">
          <h3>{t('club.nearby')}</h3>
          <ul className="LcNearby">
            {this.nearby().map((c) => (
              <li>
                <Link href={route('lc.club', { slug: c.slug() })}>{c.name()}</Link>
                <span>{c.city()}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      club.sources() && club.sources().length ? (
        <section className="LcPanel">
          <h3>{t('club.sources')}</h3>
          <ul className="LcSources">
            {club.sources().map((s) => (
              <li>
                <a href={s.url} target="_blank" rel="noopener nofollow ugc">
                  {s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null,

      !club.isVerified() ? <p className="LcNote">{t('club.unverified')}</p> : null,
    ];
  }

  /** Andere roemruchte clubs die in hetzelfde pand zaten, chronologisch. */
  sitesPanel() {
    const sites = this.club.sites() || [];
    if (!sites.length) return null;

    const years = (c) => (c.from || c.to ? (c.from === c.to ? String(c.from) : `${c.from || '?'}–${c.to || '?'}`) : '');

    return (
      <section className="LcPanel LcPanel--sites">
        <h3>{t('club.same_address')}</h3>
        {sites.map((site, i) => [
          i > 0 ? <hr className="LcSites-sep" /> : null,
          site.address ? <p className="LcSites-address">{site.address}</p> : null,
          site.building ? <p className="LcSites-building">{site.building}</p> : null,
          <ol className="LcSites">
            {site.clubs.map((c) => (
              <li className={c.current ? 'is-current' : ''}>
                <div className="LcSites-head">
                  {c.slug && !c.current ? (
                    <Link className="LcSites-name" href={route('lc.club', { slug: c.slug })}>{c.name}</Link>
                  ) : (
                    <span className="LcSites-name">{c.name}</span>
                  )}
                  {years(c) ? <span className="LcSites-years">{years(c)}</span> : null}
                </div>
                {c.note ? <p>{c.note}</p> : null}
              </li>
            ))}
          </ol>,
        ])}
      </section>
    );
  }

  // ---------------------------------------------------------------- hulpjes

  /** Naam uit de DJ-lijst; gekoppelde artiesten worden links naar hun pagina. */
  personName(p) {
    const artists = p.artists || [];
    if (!artists.length) return p.name;
    if (artists.length === 1) return <Link href={route('lc.artist', { slug: artists[0].slug })}>{p.name}</Link>;
    return artists.map((a, i) => [i ? ', ' : null, <Link href={route('lc.artist', { slug: a.slug })}>{a.name}</Link>]);
  }

  moreButton(key, anchor, total) {
    if (total <= PREVIEW[key]) return null;
    const open = this.expanded[key];
    return (
      <Button className="Button LcMore" icon={open ? 'fas fa-chevron-up' : 'fas fa-th'} onclick={() => this.toggle(key, anchor)}>
        {open ? t('club.show_less') : t('club.show_all', { count: total })}
      </Button>
    );
  }

  toggle(key, anchor) {
    this.expanded[key] = !this.expanded[key];
    // Bij inklappen terug naar het begin van de sectie, anders sta je plots veel lager.
    if (!this.expanded[key]) setTimeout(() => this.scrollTo(anchor), 0);
  }

  open(items, index) {
    this.lightbox = { items, index };
  }

  jump(e, id) {
    e.preventDefault();
    this.scrollTo(id);
  }

  scrollTo(id) {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = this.navOffset();
    window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - offset + 1, behavior: 'smooth' });
  }

  navOffset() {
    const header = document.querySelector('.App-header');
    const nav = this.element && this.element.querySelector('.LcClubNav');
    return (header ? header.offsetHeight : 0) + (nav ? nav.offsetHeight : 0) + 12;
  }

  /** Markeert in de navigatiebalk de sectie waarin de lezer zit. */
  spy() {
    if (!this.element) return;
    const offset = this.navOffset() + 40;
    let active = null;
    this.sections().forEach((s) => {
      const el = document.getElementById(s.id);
      if (el && el.getBoundingClientRect().top <= offset) active = s.id;
    });
    if (active !== this.active) {
      this.active = active;
      m.redraw();
    }
  }

  /** Tot 5 andere clubs, dichtstbijzijnde eerst (binnen ~25 km). */
  nearby() {
    const club = this.club;
    if (!club.lat()) return [];
    const rad = Math.PI / 180;
    const dist = (c) => {
      const dLat = (c.lat() - club.lat()) * rad;
      const dLng = (c.lng() - club.lng()) * rad * Math.cos(club.lat() * rad);
      return 6371 * Math.sqrt(dLat * dLat + dLng * dLng);
    };
    return app.store
      .all('lc-clubs')
      .filter((c) => c !== club && c.lat() && dist(c) < 25)
      .sort((a, b) => dist(a) - dist(b))
      .slice(0, 5);
  }

  compose() {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }
    // Een discussie heeft een primaire tag nodig. Clubtags uit een oud subforum hangen onder "Clubs";
    // de andere zijn secundaire tags, die geven we samen met de primaire tag "Clubs" mee.
    const tag = this.club.tag();
    const parent = (tag.parent && tag.parent()) || app.store.all('tags').find((t) => t.slug() === 'clubs');
    const tags = [parent, tag].filter(Boolean);
    app.composer.load(DiscussionComposer, { user: app.session.user, tags });
    app.composer.show();
  }
}
